// src/components/LockCard.tsx — one campaign price lock as the engine evaluated it (GE-03).
// Everything shown comes from ops_campaign_lock_eval via ops_*; the console computes no prices.
import type { ReactNode } from "react";
import { ACK_LABEL, TIER_LABEL, type Economics, type LockEval, type Tier } from "../lib/ops";
import { MONTHS, marginTone, money, pct } from "../lib/format";
import { useServices } from "../lib/context";

const TIERS: Tier[] = ["aprendiz", "oficial", "maestro"];

function monthName(ym: string): string {
  const m = Number(ym.slice(5, 7));
  return MONTHS[m - 1] ?? ym;
}

/** "Was $284 → $256 (10% off)" — the line the landing page will show. */
export function WasNow({ lock }: { lock: Pick<LockEval, "standard_cents" | "price_cents" | "discount_pct"> }) {
  if (lock.price_cents === null) return null;
  if (lock.standard_cents === null) return <span className="money">{money(lock.price_cents)}</span>;
  return (
    <span className="was-now">
      Was <s className="money">{money(lock.standard_cents)}</s> → <span className="money">{money(lock.price_cents)}</span>
      {lock.discount_pct > 0 && <> ({lock.discount_pct}% off)</>}
    </span>
  );
}

/** Margin at each tier. Estimated or legacy-rate margins are shown in italics as "not real" (GE-03 A5). */
export function TierChips({ economics }: { economics: Economics | null }) {
  const services = useServices();
  const target = services.data?.settings.target_margin_pct ?? 35;
  const floor = services.data?.settings.margin_floor_pct ?? 25;
  if (!economics) return null;
  const notReal = economics.basis !== "hours";
  return (
    <span className="tier-chips">
      {TIERS.map((t) => {
        const v = economics.margin_pct[t];
        const tone = notReal ? null : marginTone(v, economics.below_cost, target, floor);
        return (
          <span key={t} className="tier-chip">
            <span className="tier-name">{TIER_LABEL[t]}</span>{" "}
            <span className={`margin ${tone ?? ""} ${notReal ? "estimated" : ""}`}>{v === null ? "—" : pct(v)}</span>
          </span>
        );
      })}
      {notReal && (
        <span className="not-real">
          {economics.basis === "legacy_rate" ? "not real: legacy Maestro rate, no cost profile" : "not real: estimated at 44% materials"}
        </span>
      )}
    </span>
  );
}

/** Where each dollar goes at the Oficial rate (the headline margin): payout · kit & other costs · card fees · what's left. */
export function LockBar({ economics }: { economics: Economics | null }) {
  const services = useServices();
  const target = services.data?.settings.target_margin_pct ?? 35;
  const floor = services.data?.settings.margin_floor_pct ?? 25;
  if (!economics || !economics.price_cents) return null;
  const price = economics.price_cents;
  const payout = economics.payout_cents.oficial ?? 0;
  const kit = economics.kit_cents + economics.other_cents;
  const fee = economics.fee_cents;
  const margin = economics.margin_cents.oficial ?? price - payout - kit - fee;
  const tone = marginTone(economics.margin_pct.oficial, economics.below_cost, target, floor) ?? "ok";
  const share = (n: number) => `${Math.max(0, Math.min(100, (n / price) * 100))}%`;
  const parts = [
    { k: "payout", label: "Maestro payout", v: payout },
    { k: "kit", label: "Kit & other costs", v: kit },
    { k: "fee", label: "Card fees", v: fee },
  ];
  return (
    <div className="lock-bar-wrap">
      <div
        className={`lock-bar health-${tone}`}
        role="img"
        aria-label={`${parts.map((p) => `${p.label} ${money(p.v)}`).join(", ")}, left for Green Maestro ${money(margin)}`}
      >
        {parts.map((p) => (
          <span key={p.k} className={`seg-${p.k}`} style={{ width: share(p.v) }} />
        ))}
        <span className="seg-margin" style={{ width: share(Math.max(0, margin)) }} />
      </div>
      <p className="lock-bar-legend">
        Payout {money(payout)}, kit and other {money(kit)}, card fees {money(fee)},{" "}
        <strong className={margin < 0 ? "loss" : undefined}>{margin < 0 ? `loss ${money(-margin)}` : `left ${money(margin)}`}</strong> at the Maestro rate
      </p>
    </div>
  );
}

export function MonthNotes({ lock }: { lock: LockEval }) {
  const off = lock.months.filter((m) => !m.available).map((m) => monthName(m.month));
  const notBelow = lock.months.filter((m) => m.available && !m.lock_applies).map((m) => monthName(m.month));
  if (off.length === 0 && notBelow.length === 0) return null;
  return (
    <ul className="plain-list month-notes">
      {off.length > 0 && <li>Not bookable in {off.join(", ")}: standard rules apply there.</li>}
      {notBelow.length > 0 && <li>In {notBelow.join(", ")} the standard price is already at or below the lock, so customers pay the standard price.</li>}
    </ul>
  );
}

export function LockCard({ lock, children }: { lock: LockEval; children?: ReactNode }) {
  return (
    <div className={`lock-card ${lock.needs_ack ? "needs-ack" : ""}`}>
      <p className="lock-title">{lock.service_name}</p>
      <WasNow lock={lock} />
      <TierChips economics={lock.economics} />
      <LockBar economics={lock.economics} />
      <MonthNotes lock={lock} />
      {lock.ack_reasons.length > 0 && (
        <p className="hint warn">Needs an acknowledgment: {lock.ack_reasons.map((r) => ACK_LABEL[r]).join("; ")}.</p>
      )}
      {children}
    </div>
  );
}
